import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isUnreachable } from '@/features/leads/workflow/queue'
import { cls } from '@/ui/cls'
import { pillClass } from '@/ui/pill'
import RatingModal, { type RatingDraft } from './RatingModal'
import { trackEvent } from '@/shared/api/telemetry'
import { useClaimLead, useReleaseLead } from '@/shared/api/leads'
import { canWorkQueue, useMe } from '@/shared/api/session'
import type { LeadRow } from '@/shared/types'
import {
  type CallDraft,
  type Filters,
  type QuickView,
  QUICK_VIEWS,
  SORT_LABEL,
  isQuickViewActive,
  quickViewCount,
  quickViewFilters,
} from './columns'
import { useTableState } from './useTableState'
import TableHead from './TableHead'
import TableRow from './TableRow'
import CallLogModal from './CallLogModal'
import TableSearch from './TableSearch'
import * as m from 'motion/react-m'
import { AnimatePresence } from 'motion/react'
import { useLeadWrites } from '@/features/leads/workflow/useLeadMutations'
import { notifyFieldSaved } from '@/features/leads/workflow/writeFeedback'

// Every row is exactly this tall — measured across 6000 rows, no variation — so
// the window can be derived from scrollTop arithmetic instead of measuring each
// row. Keep in step with .lead-table td padding if that ever changes.
const ROW_H = 54
// Rows rendered beyond the viewport, above and below, so a fast scroll or a
// keyboard jump lands on real rows rather than blank space.
const OVERSCAN = 8

const clearLabel = (n: number) => `Rensa ${n} filter`

const CONTACT_OPTS: { value: Filters['contactInfo']; label: string }[] = [
  { value: 'all', label: 'Alla' },
  { value: 'reachable', label: 'Kontaktbara' },
  { value: 'unreachable', label: 'Ingen kontakt' },
]

const DNC_OPTS: { value: Filters['doNotContact']; label: string }[] = [
  { value: 'all', label: 'Alla' },
  { value: 'only', label: 'Bara Kontakta ej' },
  { value: 'hide', label: 'Dölj Kontakta ej' },
]

// The count crossfades to its new value rather than snapping — the only cue a
// filter did anything when the matching rows are off-screen. Deliberately not a
// spring: useSpring and useTransform are core hooks that cost ~5.7 kB gzipped
// on the critical path, and a counting animation implies accumulation anyway,
// which is the wrong idea for a filter result.
function Count({ value }: { value: number }) {
  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <m.strong
        key={value}
        className="inline-block"
        initial={{ opacity: 0, y: -6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 6 }}
        transition={{ duration: 0.14, ease: 'easeOut' }}
      >
        {value}
      </m.strong>
    </AnimatePresence>
  )
}

// The size of a bucket, rendered inside the button that selects it. Reads as
// part of the label rather than a separate number, so "No contact 214" is one
// thing to click and one thing to understand. Null while the full list loads.
function Badge({ value }: { value: number | null }) {
  return (
    <span className="ml-1.5 font-mono text-[10px] px-[5px] py-px rounded-full min-w-[18px] text-center bg-ink-100 text-ink-500">
      {value ?? '…'}
    </span>
  )
}

// Pinned column widths, measured off the auto-laid-out table. Windowing forces
// `table-layout: fixed`: with only the visible rows in the DOM, auto layout
// would re-measure and the columns would jump about as you scroll. The four
// narrow ones are the Calls 1-4 group, which shares one 128px header.
// One entry per rendered column, in order. Grouped the way the row is built so
// a column added or removed on one side is visible against the other: the
// score column left its 91px behind when it was removed, which handed every
// column its neighbour's width and squeezed Customer from 233 to 91.
const COL_WIDTHS = [
  // #, Score, Underlag, Customer, Län, City, Contact, Phone, Email. Score is
  // wide enough for a ring plus "· Importerad" (YCRM-41).
  49, 130, 96, 233, 93, 83, 154, 126, 145,
  // call attempts 1-4
  32, 32, 32, 32,
  // Got contact, Rating, Stage, Booked, Likely, Lead date, Move date,
  // Meeting date, Owner, Seller, SF status, Org no
  124, 106, 145, 91, 91, 105, 108, 126, 86, 143, 160, 92,
]

// Derived, never written twice: a colSpan that disagrees with the column count
// is how a full-width row ends up short or spilling past the table.
const COL_COUNT = COL_WIDTHS.length

interface Props {
  leads: LeadRow[]
  /** `leads` is the first page only and the full list is loading: counts,
   *  filters and sorting wait for it, the rows can be opened and edited. */
  partial?: boolean
  onOverlay: (id: string) => void
}

export default function TableView({ leads, partial = false, onOverlay }: Props) {
  const { updateLead, logEvent, writeError } = useLeadWrites()
  // Cached with a 5-minute staleTime and already fetched by the app shell, so
  // this is a cache read rather than a second request.
  const { data: me } = useMe()
  // Depend on `mutate`, not the mutation object. useMutation returns a fresh
  // object on every render, so a dependency on it made these callbacks new
  // every render too — which silently defeated the memo below and TableRow's
  // own, the exact thing they were written to protect. `mutate` is stable.
  const { mutate: claimMutate } = useClaimLead()
  const { mutate: releaseMutate } = useReleaseLead()
  const canClaim = canWorkQueue(me)
  const onClaim = useCallback((id: string) => { claimMutate(id) }, [claimMutate])
  // Release has no local undo of its own (it is a real server action, not an
  // optimistic cache write) — Undo re-claims the same lead for this BDR.
  const onRelease = useCallback((id: string) => {
    const lead = leads.find(l => l.id === id)
    releaseMutate(id)
    if (lead) notifyFieldSaved(`${lead.company} släppt till poolen.`, () => claimMutate(id))
  }, [leads, releaseMutate, claimMutate])
  const {
    sorts, filters, setFilter, setFilters, sorted, sortByColumn, sortFor,
    sortRank, onSortChange, clearSorts, clearFilters, activeFilterCount, values,
  } = useTableState(leads)
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [view, setView] = useState({ top: 0, height: 900 })

  // rAF-throttled: a raw scroll handler that sets state on every event would
  // reintroduce the cost windowing exists to remove.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let queued = false
    const read = () => {
      queued = false
      setView(v => (v.top === el.scrollTop && v.height === el.clientHeight
        ? v : { top: el.scrollTop, height: el.clientHeight }))
    }
    const onScroll = () => { if (!queued) { queued = true; requestAnimationFrame(read) } }
    read()
    el.addEventListener('scroll', onScroll, { passive: true })
    const ro = new ResizeObserver(onScroll)
    ro.observe(el)
    return () => { el.removeEventListener('scroll', onScroll); ro.disconnect() }
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (el && el.scrollTop !== 0) el.scrollTop = 0
  }, [filters, sorts])

  const [callDraft, setCallDraft] = useState<CallDraft | null>(null)
  const [ratingDraft, setRatingDraft] = useState<RatingDraft | null>(null)

  // Memoized so TableRow's React.memo holds — see the note there.
  const openOverlay = useCallback((id: string) => {
    const lead = leads.find(l => l.id === id)
    trackEvent('lead_opened', { from: 'table', stage: lead?.stage }, id)
    onOverlay(id)
  }, [leads, onOverlay])

  // === Modals ==========================================================
  const startRating = useCallback((lead: LeadRow, rating: number) => {
    setRatingDraft({
      leadId: lead.id,
      company: lead.company,
      rating,
      comment: lead.feedback?.comment || '',
    })
  }, [])
  const saveRating = () => {
    if (!ratingDraft) return
    const lead = leads.find(l => l.id === ratingDraft.leadId)
    if (!lead) return
    const now = new Date().toISOString()
    trackEvent('rating_set', { tier: ratingDraft.rating }, lead.id)
    updateLead(lead.id, {
      feedback: {
        rating: ratingDraft.rating,
        comment: ratingDraft.comment.trim(),
        submittedAt: now,
        submittedBy: me?.name || me?.email || 'BDR',
      },
    })
    setRatingDraft(null)
  }
  const startLogCall = useCallback((lead: LeadRow, index: number) => {
    if (lead.attempts[index]) return
    setCallDraft({
      leadId: lead.id,
      index,
      company: lead.company,
      outcome: null,
      note: '',
    })
  }, [])
  const saveLogCall = () => {
    if (!callDraft || !callDraft.outcome) return
    const lead = leads.find(l => l.id === callDraft.leadId)
    if (!lead) return
    const now = new Date().toISOString()
    const newAttempts = [...lead.attempts]
    newAttempts[callDraft.index] = now
    updateLead(lead.id, { attempts: newAttempts })
    logEvent(lead.id, {
      kind: 'call',
      outcome: callDraft.outcome,
      body: callDraft.note.trim() || undefined,
      payload: { outcome: callDraft.outcome },
    })
    setCallDraft(null)
  }

  const commitSearch = useCallback((v: string) => setFilter('global', v), [setFilter])

  const applyView = (v: QuickView) => {
    setFilters(f => ({ ...f, ...quickViewFilters(v) }))
    trackEvent('filter_applied', { column: 'quickView', view: v.id })
  }

  // Seven passes over the lead list, so not on every keystroke.
  const viewCounts = useMemo(
    () => QUICK_VIEWS.map(v => quickViewCount(leads, v)),
    [leads],
  )
  const contactCounts = useMemo(() => {
    const unreachable = leads.filter(isUnreachable).length
    return { all: leads.length, reachable: leads.length - unreachable, unreachable }
  }, [leads])
  const dncCounts = useMemo(() => {
    const marked = leads.filter(l => l.doNotContact).length
    return { all: leads.length, only: marked, hide: leads.length - marked }
  }, [leads])

  // One element per lead, rebuilt only when the rows or their handlers change.
  // Every callback it takes is already memoized for TableRow's sake, so this
  // list survives unrelated state changes — a search draft, an open modal.
  const window_ = useMemo(() => {
    const count = Math.ceil(view.height / ROW_H) + OVERSCAN * 2
    // Clamp to the end of the list. Filtering shrinks `sorted` while the
    // container keeps its old scrollTop, so an unclamped start index can sit
    // past the last row and slice to nothing — a blank table with a full-height
    // spacer, which is what a search while scrolled down used to produce.
    const maxFirst = Math.max(0, sorted.length - count)
    const first = Math.min(Math.max(0, Math.floor(view.top / ROW_H) - OVERSCAN), maxFirst)
    const last = Math.min(sorted.length, first + count)
    return { first, last, padTop: first * ROW_H, padBottom: (sorted.length - last) * ROW_H }
  }, [view, sorted.length])

  // One element per VISIBLE lead. At 6000 leads the full list was 515,000 DOM
  // nodes and 712MB of heap, which is what made the tab hang — and no amount of
  // memoisation helps, because the cost is the nodes existing at all.
  const rowElements = useMemo(
    () => sorted.slice(window_.first, window_.last).map((l, i) => (
      <TableRow
        key={l.id}
        lead={l}
        index={window_.first + i}
        myEmail={me?.email}
        onClaim={canClaim ? onClaim : undefined}
        onRelease={canClaim ? onRelease : undefined}
        onOverlay={openOverlay}
        onStartRating={startRating}
        onStartLogCall={startLogCall}
      />
    )),
    [sorted, window_, me?.email, canClaim, onClaim, onRelease, openOverlay,
     updateLead, logEvent, startRating, startLogCall],
  )

  return (
    <div className="flex flex-col h-full min-h-0 bg-white">
      {writeError && <p role="alert" className="px-4 py-2 text-xs text-[var(--color-danger)]">{writeError}</p>}
      <div
        className={cls('shrink-0 px-4 py-2.5 bg-background border-b border-ink-200 text-xs', partial && 'opacity-60')}
        inert={partial}
        aria-busy={partial}
      >
        <div className="flex items-center flex-wrap gap-2 tt-row-views">
          {QUICK_VIEWS.map((v, i) => {
            const isActive = isQuickViewActive(filters, v)
            return (
              <button
                key={v.id}
                type="button"
                aria-pressed={isActive}
                className={pillClass(isActive, v.contactInfo === 'all' ? '' : 'ml-auto')}
                onClick={() => applyView(v)}
                title={v.hint}
              >
                {v.label}
                <Badge value={partial ? null : viewCounts[i]} />
              </button>
            )
          })}
        </div>
        <div className="flex items-center flex-wrap gap-2 tt-row-main">
          <TableSearch value={filters.global} onCommit={commitSearch} />

          {/* Refines the contact-info axis without leaving the stage view the
              BDR is in — "Fresh, and only the ones I can actually call". The
              named "No contact" view above is the whole-list version. */}
          <div className="tt-contact-info" role="radiogroup" aria-label="Kontaktinfo">
            {CONTACT_OPTS.map(o => {
              const active = filters.contactInfo === o.value
              return (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={cls('tt-contact-chip', active && 'on')}
                  onClick={() => setFilter('contactInfo', o.value)}
                  title={`${contactCounts[o.value]} lead${contactCounts[o.value] === 1 ? '' : 's'}`}
                >
                  {o.label}
                  <Badge value={partial ? null : contactCounts[o.value]} />
                </button>
              )
            })}
          </div>

          {/* Kontakta ej, the GDPR mark. Alla keeps marked leads in the list,
              each with its badge. */}
          <div className="tt-contact-info" role="radiogroup" aria-label="Kontakta ej">
            {DNC_OPTS.map(o => {
              const active = filters.doNotContact === o.value
              return (
                <button
                  key={o.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={cls('tt-contact-chip', active && 'on')}
                  onClick={() => setFilter('doNotContact', o.value)}
                  title={`${dncCounts[o.value]} lead${dncCounts[o.value] === 1 ? '' : 's'}`}
                >
                  {o.label}
                  <Badge value={partial ? null : dncCounts[o.value]} />
                </button>
              )
            })}
          </div>

          {sorts.length > 1 && (
            <div className="flex items-center gap-1 text-[11px] text-ink-500">
              <span className="uppercase tracking-[0.08em]">Sortering</span>
              {sorts.map((s, i) => (
                <span key={s.key} className="flex items-center gap-1">
                  {i > 0 && <span className="text-ink-500">sedan</span>}
                  <span className="text-ink-1000">
                    {SORT_LABEL[s.key] ?? s.key} {s.dir === 'asc' ? '↑' : '↓'}
                  </span>
                </span>
              ))}
              <button
                type="button"
                onClick={clearSorts}
                className="ml-0.5 bg-transparent border-0 p-0 text-ink-500 hover:text-ink-1000 cursor-pointer underline"
                title="Tillbaka till standardsorteringen"
              >
                återställ
              </button>
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            {partial ? (
              <span className="text-ink-500 text-xs">Laddar alla leads…</span>
            ) : (
              <span className="text-ink-500 text-xs font-mono tabular-nums">
                <Count value={sorted.length} /> / {leads.length}
              </span>
            )}
            <AnimatePresence>
              {activeFilterCount > 0 && (
                <m.button
                  type="button"
                  className={pillClass(false)}
                  onClick={clearFilters}
                  title="Återställ alla aktiva filter"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.14, ease: 'easeOut' }}
                >
                  {clearLabel(activeFilterCount)}
                </m.button>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto" ref={scrollRef}>
      <table className="lead-table lead-table-sticky lead-table-windowed">
        <colgroup>
          {COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
        </colgroup>
        <TableHead
          inert={partial}
          sorts={sorts}
          sortByColumn={sortByColumn}
          sortFor={sortFor}
          sortRank={sortRank}
          onSortChange={onSortChange}
          filters={filters}
          setFilter={setFilter}
          values={values}
        />
        <tbody>
          {sorted.length === 0 && (
            <tr>
              {/* Opacity only. A transform on a table cell fights the table
                  layout algorithm, and this is the one row that appears
                  exactly when the windowed list is empty. */}
              <m.td
                colSpan={COL_COUNT}
                className="table-empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.18, ease: 'easeOut' }}
              >
                Inga leads matchar de aktuella filtren.
                {activeFilterCount > 0 && (
                  <>
                    {' '}
                    <button type="button" className="bg-none border-0 p-0 text-ink-1000 underline cursor-pointer font-[inherit]" onClick={clearFilters}>{clearLabel(activeFilterCount)}</button>
                  </>
                )}
              </m.td>
            </tr>
          )}
          {window_.padTop > 0 && (
            <tr aria-hidden="true" style={{ height: window_.padTop }}>
              <td colSpan={COL_COUNT} />
            </tr>
          )}
          {rowElements}
          {window_.padBottom > 0 && (
            <tr aria-hidden="true" style={{ height: window_.padBottom }}>
              <td colSpan={COL_COUNT} />
            </tr>
          )}
        </tbody>
      </table>
      </div>

      <AnimatePresence>
        {callDraft && (
          <CallLogModal
            draft={callDraft}
            setDraft={setCallDraft}
            onCancel={() => setCallDraft(null)}
            onSave={saveLogCall}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {ratingDraft && (
          <RatingModal
            draft={ratingDraft}
            setDraft={setRatingDraft}
            onCancel={() => setRatingDraft(null)}
            onSave={saveRating}
          />
        )}
      </AnimatePresence>
    </div>
  )
}
